import { useState, useEffect, useRef } from "react";

// ─── Anthropic API ────────────────────────────────────────────────────────────
const API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || "";

async function callClaude(system, user, onChunk, tools) {
  if (!API_KEY) throw new Error("No API key set. Add REACT_APP_ANTHROPIC_API_KEY to your .env file.");
  const body = {
    model: "claude-sonnet-4-20250514",
    max_tokens: 1000,
    stream: true,
    system,
    messages: [{ role: "user", content: user }],
  };
  if (tools) { body.tools = tools; body.stream = false; delete body.stream; }

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": API_KEY,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({ ...body, stream: !tools }),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);

  if (tools) {
    const data = await res.json();
    return data.content;
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const line of dec.decode(value).split("\n")) {
      if (!line.startsWith("data: ")) continue;
      const d = line.slice(6).trim();
      if (d === "[DONE]") continue;
      try {
        const j = JSON.parse(d);
        if (j.type === "content_block_delta" && j.delta?.text) {
          full += j.delta.text;
          onChunk?.(full);
        }
      } catch {}
    }
  }
  return full;
}

async function callClaudeWithSearch(system, user, onChunk) {
  if (!API_KEY) throw new Error("No API key set. Add REACT_APP_ANTHROPIC_API_KEY to your .env file.");
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": API_KEY,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system,
      messages: [{ role: "user", content: user }],
      tools: [{ type: "web_search_20250305", name: "web_search" }],
    }),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  const data = await res.json();
  const text = data.content
    .filter(b => b.type === "text")
    .map(b => b.text)
    .join("\n");
  onChunk?.(text);
  return text;
}

// ─── File upload utilities ────────────────────────────────────────────────────
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

async function extractFileText(file) {
  const ext = file.name.toLowerCase().split(".").pop();
  if (["txt", "rtf", "md", "csv"].includes(ext)) {
    return readFileAsText(file);
  }
  if (ext === "pdf") {
    if (!API_KEY) throw new Error("API key required to extract PDF text.");
    const base64 = await readFileAsBase64(file);
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 8000,
        messages: [{
          role: "user",
          content: [
            {
              type: "document",
              source: { type: "base64", media_type: "application/pdf", data: base64 },
            },
            {
              type: "text",
              text: "Extract all the text from this document exactly as written. Preserve structure — use line breaks to separate sections. Return only the extracted text, no commentary.",
            },
          ],
        }],
      }),
    });
    if (!res.ok) throw new Error(`PDF read failed (${res.status})`);
    const data = await res.json();
    const text = data.content?.find(b => b.type === "text")?.text;
    if (!text) throw new Error("No text could be extracted from this PDF.");
    return text;
  }
  throw new Error("Unsupported format. Use PDF or TXT — or paste your text directly.");
}

// ─── Persistent storage helpers ───────────────────────────────────────────────
async function storeGet(key) {
  try { const r = await window.storage.get(key); return r ? JSON.parse(r.value) : null; }
  catch { return null; }
}
async function storeSet(key, val) {
  try { await window.storage.set(key, JSON.stringify(val)); } catch {}
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:      "#0B0F1A",
  surface: "#131929",
  card:    "#1C2540",
  border:  "#2A3556",
  borderL: "#3D4F7A",
  ink:     "#E8EDF8",
  inkMid:  "#8B9CC8",
  inkDim:  "#4A5578",
  gold:    "#F0C040",
  goldD:   "#B88A20",
  teal:    "#3DD6C8",
  tealD:   "#1A8F87",
  violet:  "#9B7FE8",
  red:     "#F05060",
  green:   "#40D080",
  amber:   "#F09030",
};

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@300;400;500&family=Outfit:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: ${C.bg}; color: ${C.ink}; font-family: 'Outfit', sans-serif; }
  ::selection { background: ${C.gold}33; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: ${C.surface}; }
  ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
  textarea, input { font-family: 'Outfit', sans-serif; color: ${C.ink}; }
  button { cursor: pointer; font-family: 'Outfit', sans-serif; }

  @keyframes fadeUp   { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn   { from { opacity:0; } to { opacity:1; } }
  @keyframes spin     { to { transform: rotate(360deg); } }
  @keyframes pulse    { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
  @keyframes shimmer  { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes slideR   { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
  @keyframes glow     { 0%,100% { box-shadow: 0 0 12px ${C.gold}44; } 50% { box-shadow: 0 0 28px ${C.gold}88; } }

  .fu  { animation: fadeUp 0.45s cubic-bezier(.16,1,.3,1) both; }
  .fi  { animation: fadeIn 0.3s ease both; }
  .sr  { animation: slideR 0.35s cubic-bezier(.16,1,.3,1) both; }
  .ad1 { animation-delay: 0.05s; }
  .ad2 { animation-delay: 0.12s; }
  .ad3 { animation-delay: 0.20s; }
  .ad4 { animation-delay: 0.28s; }
`;

// ─── Primitives ───────────────────────────────────────────────────────────────
const Spinner = ({ size = 18, color = C.gold }) => (
  <div style={{ width: size, height: size, border: `2px solid ${C.border}`, borderTopColor: color,
    borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
);

const Tag = ({ label, color = C.teal }) => (
  <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 20, fontSize: 11,
    fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase",
    color: C.bg, background: color }}>{label}</span>
);

const Card = ({ children, style = {}, className = "" }) => (
  <div className={className} style={{ background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 12, padding: 24, ...style }}>{children}</div>
);

function Btn({ children, onClick, variant = "primary", disabled, small, icon, full }) {
  const v = {
    primary:   { bg: C.gold,    fg: C.bg,      hover: C.goldD },
    secondary: { bg: "transparent", fg: C.inkMid, hover: C.border, bdr: C.border },
    teal:      { bg: C.teal,    fg: C.bg,      hover: C.tealD },
    ghost:     { bg: `${C.teal}18`, fg: C.teal, hover: `${C.teal}30` },
    danger:    { bg: `${C.red}22`, fg: C.red,   hover: `${C.red}35` },
    violet:    { bg: C.violet,  fg: "white",   hover: "#7A60C0" },
  }[variant];
  const [hov, setHov] = useState(false);
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: 7, padding: small ? "6px 14px" : "10px 22px",
        fontSize: small ? 12 : 14, fontWeight: 600, letterSpacing: "0.02em",
        borderRadius: 8, border: v.bdr ? `1px solid ${v.bdr}` : "none",
        background: hov && !disabled ? v.hover : v.bg, color: v.fg,
        opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s", width: full ? "100%" : undefined,
      }}>
      {icon && <span style={{ fontSize: small ? 13 : 15 }}>{icon}</span>}{children}
    </button>
  );
}

function TA({ label, hint, value, onChange, rows = 8, placeholder, mono }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 600, color: C.inkMid,
        letterSpacing: "0.09em", textTransform: "uppercase" }}>{label}</label>}
      {hint && <p style={{ fontSize: 12, color: C.inkDim, fontStyle: "italic" }}>{hint}</p>}
      <textarea value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        style={{
          width: "100%", padding: "12px 14px", resize: "vertical",
          border: `1px solid ${C.border}`, borderRadius: 8,
          background: C.surface, color: C.ink, fontSize: mono ? 12 : 14,
          fontFamily: mono ? "'DM Mono', monospace" : "'Outfit', sans-serif",
          lineHeight: 1.65, transition: "border-color 0.2s",
        }}
        onFocus={e => e.target.style.borderColor = C.gold}
        onBlur={e => e.target.style.borderColor = C.border}
      />
    </div>
  );
}

function StreamBox({ text, done, maxH }) {
  const ref = useRef();
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [text]);
  return (
    <div ref={ref} style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "16px 18px", fontSize: 13, lineHeight: 1.8, color: C.ink,
      whiteSpace: "pre-wrap", wordBreak: "break-word",
      maxHeight: maxH || 480, overflowY: "auto",
      fontFamily: "'Outfit', sans-serif",
    }}>
      {text || <span style={{ color: C.inkDim, fontStyle: "italic" }}>Generating…</span>}
      {!done && text && <span style={{ animation: "pulse 1s infinite", display: "inline-block",
        marginLeft: 2, color: C.gold }}>▍</span>}
    </div>
  );
}

function ScoreBar({ score, max = 5, label }) {
  const pct = (score / max) * 100;
  const col = pct >= 80 ? C.green : pct >= 50 ? C.amber : C.red;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {label && <span style={{ fontSize: 12, color: C.inkMid, width: 64, flexShrink: 0,
        textTransform: "capitalize" }}>{label}</span>}
      <div style={{ flex: 1, height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: col, borderRadius: 3,
          transition: "width 0.7s cubic-bezier(.16,1,.3,1)" }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: col, width: 28, textAlign: "right",
        fontFamily: "'DM Mono', monospace" }}>{score}/{max}</span>
    </div>
  );
}

function CopyBtn({ text, small }) {
  const [cp, setCp] = useState(false);
  return (
    <Btn small={small !== false} variant="secondary" onClick={() => {
      navigator.clipboard.writeText(text); setCp(true);
      setTimeout(() => setCp(false), 2000);
    }}>{cp ? "✓ Copied" : "Copy"}</Btn>
  );
}

function FileUploadBtn({ onText, fieldLabel }) {
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [fileName, setFileName] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const inputRef = useRef();

  async function handleChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus("loading");
    setFileName(file.name);
    setErrMsg("");
    try {
      const text = await extractFileText(file);
      onText(text);
      setStatus("done");
    } catch (err) {
      setErrMsg(err.message);
      setStatus("error");
      setFileName("");
    }
    e.target.value = "";
  }

  const isDone = status === "done";
  const isLoading = status === "loading";

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 5 }}>
      <input ref={inputRef} type="file" accept=".pdf,.txt,.rtf,.md"
        style={{ display: "none" }} onChange={handleChange} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={() => { setStatus("idle"); setFileName(""); setErrMsg(""); inputRef.current?.click(); }}
          disabled={isLoading}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "6px 14px", borderRadius: 8,
            border: `1px dashed ${isDone ? C.green : C.borderL}`,
            background: isDone ? `${C.green}12` : "transparent",
            color: isDone ? C.green : C.inkMid,
            fontSize: 12, fontWeight: 600,
            cursor: isLoading ? "wait" : "pointer",
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { if (!isLoading && !isDone) { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}}
          onMouseLeave={e => { if (!isDone) { e.currentTarget.style.borderColor = C.borderL; e.currentTarget.style.color = C.inkMid; }}}
        >
          {isLoading
            ? <><Spinner size={12} color={C.gold} />&nbsp;Reading {fieldLabel}…</>
            : isDone
              ? <>✓ {fileName}</>
              : <>📎 Upload {fieldLabel}</>}
        </button>

        {!isLoading && !isDone && (
          <span style={{ fontSize: 11, color: C.inkDim, fontStyle: "italic" }}>
            PDF or TXT · Word docs: save as PDF first
          </span>
        )}
        {isDone && (
          <button
            onClick={() => { setStatus("idle"); setFileName(""); onText(""); }}
            style={{ fontSize: 11, color: C.inkDim, background: "none", border: "none",
              cursor: "pointer", textDecoration: "underline" }}>
            Clear
          </button>
        )}
      </div>
      {status === "error" && (
        <p style={{ fontSize: 12, color: C.red, margin: 0 }}>⚠ {errMsg}</p>
      )}
    </div>
  );
}

// ─── Supporting document entry ────────────────────────────────────────────────
function SupportingDocEntry({ doc, onUpdate, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const inputRef = useRef();

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErrMsg("");
    try {
      const text = await extractFileText(file);
      onUpdate({ text, fileName: file.name });
    } catch (err) {
      setErrMsg(err.message);
    }
    setUploading(false);
    e.target.value = "";
  }

  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      padding: "10px 0", borderTop: `1px solid ${C.border}`, marginTop: 6,
    }}>
      <input
        list={`sdl-${doc.id}`}
        value={doc.label}
        onChange={e => onUpdate({ label: e.target.value })}
        placeholder="Label — e.g. Last Appraisal, Interview Feedback…"
        style={{
          flex: 1, minWidth: 180, padding: "6px 10px", borderRadius: 7,
          border: `1px solid ${C.border}`, background: C.card,
          color: C.ink, fontSize: 12, fontFamily: "'Outfit',sans-serif",
        }}
      />
      <datalist id={`sdl-${doc.id}`}>
        {["Last Appraisal","Interview Feedback","Work Example","Reference","Competency Evidence","Personal Development Plan"].map(l => (
          <option key={l} value={l} />
        ))}
      </datalist>

      <input ref={inputRef} type="file" accept=".pdf,.txt,.rtf,.md"
        style={{ display: "none" }} onChange={handleFile} />

      {doc.text ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: C.green }}>✓ {doc.fileName}</span>
          <button onClick={() => { onUpdate({ text: "", fileName: "" }); inputRef.current?.click(); }}
            style={{ fontSize: 11, color: C.inkDim, background: "none", border: "none",
              cursor: "pointer", textDecoration: "underline" }}>Replace</button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} disabled={uploading}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 12px", borderRadius: 7, border: `1px dashed ${C.borderL}`,
            background: "transparent", color: C.inkMid, fontSize: 12, fontWeight: 600,
            cursor: uploading ? "wait" : "pointer",
          }}>
          {uploading ? <><Spinner size={11} color={C.teal} />&nbsp;Reading…</> : "📎 Upload file"}
        </button>
      )}

      <button onClick={onRemove} style={{
        width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`,
        background: "transparent", color: C.inkDim, fontSize: 16,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>×</button>

      {errMsg && <p style={{ width: "100%", fontSize: 11, color: C.red, margin: 0 }}>⚠ {errMsg}</p>}
    </div>
  );
}

function TabBar({ tabs, active, onSelect }) {
  return (
    <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${C.border}`,
      marginBottom: 28, overflowX: "auto" }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onSelect(t.id)} style={{
          padding: "10px 18px", border: "none", background: "none",
          fontSize: 13, fontWeight: 600, color: active === t.id ? C.gold : C.inkMid,
          borderBottom: `2px solid ${active === t.id ? C.gold : "transparent"}`,
          marginBottom: -1, transition: "all 0.15s", whiteSpace: "nowrap",
          letterSpacing: "0.02em",
        }}>
          {t.icon} {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── FIT SCORE badge ──────────────────────────────────────────────────────────
function FitScore({ score }) {
  const col = score >= 80 ? C.green : score >= 60 ? C.amber : score >= 40 ? C.gold : C.red;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", width: 56, height: 56, borderRadius: "50%",
      border: `2px solid ${col}`, flexShrink: 0 }}>
      <span style={{ fontSize: 16, fontWeight: 700, color: col,
        fontFamily: "'DM Mono', monospace" }}>{score}</span>
      <span style={{ fontSize: 9, color: C.inkDim, textTransform: "uppercase",
        letterSpacing: "0.06em" }}>fit</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 1 — INPUT
// ═══════════════════════════════════════════════════════════════════════════════
function StepInput({ onAnalyse, savedSession }) {
  const [cv, setCv] = useState(savedSession?.cv || "");
  const [advert, setAdvert] = useState(savedSession?.advert || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [showSupportingDocs, setShowSupportingDocs] = useState(false);
  const ready = cv.trim().length > 80 && advert.trim().length > 80;

  function addSupportingDoc(label = "") {
    setSupportingDocs(prev => [...prev, { id: Date.now(), label, text: "", fileName: "" }]);
    setShowSupportingDocs(true);
  }
  function updateSupportingDoc(id, changes) {
    setSupportingDocs(prev => prev.map(d => d.id === id ? { ...d, ...changes } : d));
  }
  function removeSupportingDoc(id) {
    setSupportingDocs(prev => prev.filter(d => d.id !== id));
  }

  async function go() {
    setLoading(true); setErr("");
    try {
      const sys = `You are a UK Civil Service recruitment expert. Analyse the CV and job advert and return ONLY valid JSON, no markdown:
{
  "candidateName": "string",
  "roleTitle": "string",
  "department": "string",
  "grade": "string",
  "salary": "string",
  "closingDate": "string",
  "refNumber": "string or null",
  "location": "string",
  "behaviours": ["string"],
  "essentialCriteria": ["string"],
  "desirableCriteria": ["string"],
  "technicalAreas": ["string"],
  "keyStrengths": ["string — specific, include figures from CV"],
  "gaps": ["string"],
  "searchTerms": ["4-6 search terms for finding similar roles on Civil Service Jobs"],
  "equivalentTitles": ["3-5 equivalent job titles this candidate should search for"],
  "summary": "string — 2-3 sentences on fit"
}
If additional context documents are provided, use them to enrich keyStrengths, gaps, and summary.`;
      const loadedDocs = supportingDocs.filter(d => d.text);
      const docsContext = loadedDocs.length > 0
        ? "\n\nADDITIONAL CONTEXT DOCUMENTS:\n" + loadedDocs.map(d => `[${d.label || "Document"}]:\n${d.text}`).join("\n\n")
        : "";
      const raw = await callClaude(sys, `CV:\n${cv}\n\nJOB ADVERT:\n${advert}${docsContext}`, null);
      const parsed = JSON.parse(raw.replace(/```json|```/g, "").trim());
      const session = { cv, advert, brief: parsed, supportingDocs };
      await storeSet("session", session);
      onAnalyse(session);
    } catch (e) { setErr("Analysis failed: " + e.message); }
    setLoading(false);
  }

  return (
    <div className="fu" style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Hero */}
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
          padding: "5px 16px", borderRadius: 20, border: `1px solid ${C.gold}44`,
          background: `${C.gold}11`, marginBottom: 20 }}>
          <span style={{ color: C.gold, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.1em", textTransform: "uppercase" }}>Civil Service Career Platform</span>
        </div>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 48,
          color: C.ink, lineHeight: 1.15, marginBottom: 16 }}>
          Find. Prepare.<br />
          <span style={{ color: C.gold, fontStyle: "italic" }}>Land the role.</span>
        </h1>
        <p style={{ color: C.inkMid, fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.7 }}>
          Paste your CV and one job advert. The platform analyses your profile, finds matching roles across Civil Service Jobs, tailors your documents for each, and coaches you through the interview.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card>
          <TA label="Your CV" placeholder="Paste your full CV — all roles, dates, achievements and figures…"
            value={cv} onChange={setCv} rows={14}
            hint="Include specific figures: budget sizes, team sizes, recovery amounts, percentages." />
          <FileUploadBtn fieldLabel="CV" onText={setCv} />
        </Card>
        <Card>
          <TA label="Job Advert" placeholder="Paste the full job advert — job description, essential criteria, behaviours…"
            value={advert} onChange={setAdvert} rows={14}
            hint="The more complete the advert, the better the analysis and document tailoring." />
          <FileUploadBtn fieldLabel="Job Advert" onText={setAdvert} />
        </Card>
      </div>

      {/* Supporting Documents — optional extra context */}
      <div style={{ marginBottom: 16 }}>
        {!showSupportingDocs ? (
          <button
            onClick={() => setShowSupportingDocs(true)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", padding: "8px 14px", borderRadius: 8,
              border: `1px dashed ${C.borderL}`, background: "transparent",
              color: C.inkDim, fontSize: 12, fontWeight: 600, cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.teal; e.currentTarget.style.color = C.teal; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.borderL; e.currentTarget.style.color = C.inkDim; }}
          >
            📎 Add supporting documents
            <span style={{ fontWeight: 400, opacity: 0.7 }}>— appraisals · feedback · work examples · optional</span>
            {supportingDocs.filter(d => d.text).length > 0 && (
              <span style={{ color: C.teal, fontWeight: 700 }}> · {supportingDocs.filter(d => d.text).length} loaded</span>
            )}
          </button>
        ) : (
          <Card style={{ background: C.surface }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: C.inkMid,
                textTransform: "uppercase", letterSpacing: "0.09em", margin: 0 }}>
                📎 Supporting Documents
              </p>
              <button onClick={() => setShowSupportingDocs(false)}
                style={{ fontSize: 11, color: C.inkDim, background: "none", border: "none", cursor: "pointer" }}>
                ▼ collapse
              </button>
            </div>
            <p style={{ fontSize: 12, color: C.inkDim, fontStyle: "italic", marginBottom: 14 }}>
              Upload past appraisals, interview feedback, work examples or references. These give Claude richer context about your evidence, strengths and development areas.
            </p>

            {/* Quick-add chips */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: supportingDocs.length > 0 ? 4 : 0 }}>
              {["Last Appraisal", "Interview Feedback", "Work Example", "Reference", "Competency Evidence"].map(l => (
                !supportingDocs.find(d => d.label === l) && (
                  <button key={l} onClick={() => addSupportingDoc(l)}
                    style={{
                      padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                      border: `1px solid ${C.teal}44`, background: `${C.teal}10`,
                      color: C.teal, cursor: "pointer",
                    }}>
                    + {l}
                  </button>
                )
              ))}
              <button onClick={() => addSupportingDoc("")}
                style={{
                  padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                  border: `1px solid ${C.border}`, background: "transparent",
                  color: C.inkDim, cursor: "pointer",
                }}>
                + Custom
              </button>
            </div>

            {supportingDocs.map(doc => (
              <SupportingDocEntry
                key={doc.id}
                doc={doc}
                onUpdate={changes => updateSupportingDoc(doc.id, changes)}
                onRemove={() => removeSupportingDoc(doc.id)}
              />
            ))}
          </Card>
        )}
      </div>

      {err && (
        <div style={{ background: `${C.red}18`, border: `1px solid ${C.red}44`,
          borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: C.red, fontSize: 13 }}>
          {err}
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        {loading
          ? <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
              <Spinner /><span style={{ color: C.inkMid, fontSize: 14, fontStyle: "italic" }}>Analysing your profile…</span>
            </div>
          : <Btn onClick={go} disabled={!ready} icon="→">Analyse & Build My Platform</Btn>
        }
      </div>

      {/* Feature pills */}
      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 36, flexWrap: "wrap" }}>
        {["📊 Profile Brief","📄 Document Generator","🔍 Job Finder","📋 Application Tracker","🎤 Interview Coach"].map(f => (
          <div key={f} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${C.border}`,
            fontSize: 12, color: C.inkMid }}>{f}</div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BRIEF PANEL
// ═══════════════════════════════════════════════════════════════════════════════
function BriefPanel({ brief }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

      {/* Hero card */}
      <Card className="fu" style={{ gridColumn: "1/-1",
        background: `linear-gradient(135deg, ${C.card} 0%, #1A2040 100%)`,
        border: `1px solid ${C.gold}44`, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160,
          borderRadius: "50%", background: `${C.gold}08` }} />
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 20, position: "relative" }}>
          <div>
            <p style={{ fontSize: 11, color: C.gold, letterSpacing: "0.1em",
              textTransform: "uppercase", marginBottom: 6 }}>Candidate</p>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, color: C.ink }}>
              {brief.candidateName}</h2>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 11, color: C.gold, letterSpacing: "0.1em",
              textTransform: "uppercase", marginBottom: 6 }}>Current Target Role</p>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: C.ink }}>
              {brief.roleTitle}</h2>
            <p style={{ color: C.teal, fontSize: 14, marginTop: 3 }}>
              {brief.department}{brief.grade ? ` · ${brief.grade}` : ""}
            </p>
          </div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {brief.salary && <div><p style={{ fontSize: 11, color: C.inkDim, textTransform: "uppercase", letterSpacing: "0.07em" }}>Salary</p><p style={{ color: C.ink, fontWeight: 600 }}>{brief.salary}</p></div>}
            {brief.closingDate && <div><p style={{ fontSize: 11, color: C.inkDim, textTransform: "uppercase", letterSpacing: "0.07em" }}>Closes</p><p style={{ color: C.red, fontWeight: 600 }}>{brief.closingDate}</p></div>}
            {brief.location && <div><p style={{ fontSize: 11, color: C.inkDim, textTransform: "uppercase", letterSpacing: "0.07em" }}>Location</p><p style={{ color: C.ink, fontWeight: 600 }}>{brief.location}</p></div>}
          </div>
        </div>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`,
          position: "relative" }}>
          <p style={{ color: C.inkMid, fontSize: 14, lineHeight: 1.7, fontStyle: "italic" }}>{brief.summary}</p>
        </div>
      </Card>

      {/* Behaviours */}
      <Card className="fu ad1">
        <p style={{ fontSize: 11, fontWeight: 700, color: C.teal, letterSpacing: "0.09em",
          textTransform: "uppercase", marginBottom: 14 }}>Assessed Behaviours</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {(brief.behaviours || []).map((b, i) => <Tag key={i} label={b} color={C.teal} />)}
        </div>
        {brief.technicalAreas?.length > 0 && <>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.inkDim, letterSpacing: "0.09em",
            textTransform: "uppercase", marginBottom: 10 }}>Technical Areas</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {brief.technicalAreas.map((t, i) => <Tag key={i} label={t} color={C.violet} />)}
          </div>
        </>}
      </Card>

      {/* Strengths + Gaps */}
      <Card className="fu ad2">
        <p style={{ fontSize: 11, fontWeight: 700, color: C.green, letterSpacing: "0.09em",
          textTransform: "uppercase", marginBottom: 12 }}>Key Strengths for This Role</p>
        <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {(brief.keyStrengths || []).map((s, i) => (
            <li key={i} style={{ display: "flex", gap: 10, fontSize: 13, lineHeight: 1.5 }}>
              <span style={{ color: C.green, fontWeight: 700 }}>✓</span>
              <span style={{ color: C.inkMid }}>{s}</span>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 11, fontWeight: 700, color: C.amber, letterSpacing: "0.09em",
          textTransform: "uppercase", marginBottom: 10 }}>Areas to Address</p>
        {(brief.gaps || []).map((g, i) => (
          <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, lineHeight: 1.5, marginBottom: 6 }}>
            <span style={{ color: C.amber }}>△</span>
            <span style={{ color: C.inkMid }}>{g}</span>
          </div>
        ))}
      </Card>

      {/* Essential Criteria */}
      <Card className="fu ad3" style={{ gridColumn: "1/-1" }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: C.gold, letterSpacing: "0.09em",
          textTransform: "uppercase", marginBottom: 14 }}>Essential Criteria</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 32px" }}>
          {(brief.essentialCriteria || []).map((c, i) => (
            <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, lineHeight: 1.55 }}>
              <span style={{ color: C.gold, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                fontSize: 11, marginTop: 2 }}>{String(i+1).padStart(2,"0")}</span>
              <span style={{ color: C.inkMid }}>{c}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Search terms */}
      {(brief.equivalentTitles?.length > 0 || brief.searchTerms?.length > 0) && (
        <Card className="fu ad4" style={{ gridColumn: "1/-1" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.violet, letterSpacing: "0.09em",
            textTransform: "uppercase", marginBottom: 12 }}>Recommended Job Search Terms</p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[...(brief.equivalentTitles||[]), ...(brief.searchTerms||[])].map((t,i) => (
              <Tag key={i} label={t} color={C.violet} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT GENERATOR
// ═══════════════════════════════════════════════════════════════════════════════
function DocGenerator({ session, targetAdvert, targetRole }) {
  const { cv, advert: baseAdvert, brief, supportingDocs } = session;
  const workingAdvert = targetAdvert || baseAdvert;
  const workingRole   = targetRole || brief.roleTitle;

  const loadedDocs = (supportingDocs || []).filter(d => d.text);
  const extraContext = loadedDocs.length > 0
    ? "\n\nADDITIONAL CANDIDATE CONTEXT (from uploaded supporting documents):\n" +
      loadedDocs.map(d => `[${d.label || "Document"}]:\n${d.text}`).join("\n\n")
    : "";

  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [done, setDone] = useState(false);

  const docs = [
    {
      id: "ps", icon: "📄", label: "Personal Statement",
      color: C.gold,
      desc: "~1,000 word statement of suitability mapped to this role's essential criteria",
      prompt: `Write a high-quality UK Civil Service personal statement (~950-1000 words) for this candidate applying for this role.

CV: ${cv}
JOB ADVERT: ${workingAdvert}
CANDIDATE BRIEF: ${JSON.stringify(brief)}

Rules:
- Open with a confident, evidence-led paragraph — no "I am excited/passionate/delighted"
- Each paragraph addresses a specific essential criterion
- Use real figures and specific examples from the CV
- Use "I" not "we" throughout
- End with a concise, evidence-based close — not aspirational waffle
- Remove all personal identifying information (name-blind)
- No header, no title — just the statement text${extraContext}`,
    },
    {
      id: "behaviour", icon: "🎯", label: "Behaviour Bank",
      color: C.teal,
      desc: "Full STAR ~250 words for each assessed behaviour, ready to adapt",
      prompt: `Write a complete Behaviour Evidence Bank for a UK Civil Service application.

CV: ${cv}
BEHAVIOURS TO COVER: ${(brief.behaviours||[]).join(", ")}
ROLE: ${workingRole}

For each behaviour:
## [Behaviour Name]
**Situation:** [context, 2-3 sentences]
**Task:** [your specific responsibility, 2-3 sentences]
**Action:** [what YOU did — 4-6 specific sentences, always "I" not "we"]
**Result:** [quantified impact, 2-3 sentences]

Draw only on real experience visible in the CV. Include specific figures where available.${extraContext}`,
    },
    {
      id: "cv", icon: "📋", label: "Optimised CV",
      color: C.violet,
      desc: "Rewritten with quantified achievements and language matched to this advert",
      prompt: `Rewrite this CV for a UK Civil Service ${workingRole} application.

ORIGINAL CV: ${cv}
TARGET ADVERT: ${workingAdvert}
ESSENTIAL CRITERIA: ${(brief.essentialCriteria||[]).join("; ")}

Rules:
- Every bullet = an achievement, not a duty
- Add all specific figures that exist in the original CV (budgets, percentages, timescales, team sizes)
- Match language to the essential criteria
- Current role gets the most detail
- Format: Role Title | Organisation | Dates then bullet points
- ~2 pages of content${extraContext}`,
    },
    {
      id: "cover", icon: "✉️", label: "Cover Letter",
      color: C.amber,
      desc: "Concise, specific cover letter for roles that require one",
      prompt: `Write a concise, high-impact cover letter for this application.

CV: ${cv}
JOB ADVERT: ${workingAdvert}
CANDIDATE BRIEF: ${JSON.stringify(brief)}

Rules:
- 4 paragraphs, approximately 350 words total
- Para 1: direct statement of what you are applying for and your strongest single credential
- Para 2: most relevant and quantified achievement that directly matches the role's top requirement
- Para 3: second specific example showing a different dimension of the role
- Para 4: confident close with a specific hook — reference something in the advert or department
- No generic phrases ("I am writing to express my interest")
- Address to "Dear Hiring Manager" — no name${extraContext}`,
    },
  ];

  async function generate(doc) {
    if (streaming) return;
    setActive(doc.id); setText(""); setDone(false); setStreaming(true);
    try {
      await callClaude(
        "You are an expert UK Civil Service application writer. Write exactly what is requested. No preamble, no meta-commentary, no markdown code blocks.",
        doc.prompt,
        chunk => setText(chunk)
      );
      setDone(true);
    } catch (e) {
      setText("Error: " + e.message); setDone(true);
    }
    setStreaming(false);
  }

  return (
    <div className="fu">
      {targetRole && (
        <div style={{ padding: "10px 14px", background: `${C.teal}18`,
          border: `1px solid ${C.teal}44`, borderRadius: 8, marginBottom: 12,
          fontSize: 13, color: C.teal }}>
          🎯 Generating documents tailored for: <strong>{targetRole}</strong>
        </div>
      )}
      {loadedDocs.length > 0 && (
        <div style={{ padding: "8px 14px", background: `${C.violet}12`,
          border: `1px solid ${C.violet}33`, borderRadius: 8, marginBottom: 20,
          fontSize: 12, color: C.violet, display: "flex", alignItems: "center", gap: 8 }}>
          📎 <strong>{loadedDocs.length} supporting document{loadedDocs.length > 1 ? "s" : ""}</strong> loaded as extra context:&nbsp;
          {loadedDocs.map(d => d.label || "Document").join(" · ")}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {docs.map(doc => (
          <button key={doc.id} onClick={() => generate(doc)}
            disabled={streaming && active !== doc.id}
            style={{
              padding: "16px 18px", borderRadius: 10,
              border: `1.5px solid ${active === doc.id ? doc.color : C.border}`,
              background: active === doc.id ? `${doc.color}12` : C.card,
              textAlign: "left", transition: "all 0.15s",
              opacity: streaming && active !== doc.id ? 0.4 : 1,
              cursor: streaming && active !== doc.id ? "not-allowed" : "pointer",
            }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 20 }}>{doc.icon}</span>
              <span style={{ fontWeight: 700, color: active === doc.id ? doc.color : C.ink,
                fontSize: 14 }}>{doc.label}</span>
              {active === doc.id && streaming && <Spinner size={14} color={doc.color} />}
            </div>
            <p style={{ fontSize: 12, color: C.inkDim, lineHeight: 1.45 }}>{doc.desc}</p>
          </button>
        ))}
      </div>

      {active && (
        <Card className="sr" style={{ borderLeft: `3px solid ${docs.find(d=>d.id===active)?.color}` }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>{docs.find(d=>d.id===active)?.icon}</span>
              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: C.ink }}>
                {docs.find(d=>d.id===active)?.label}
              </h3>
            </div>
            {done && <div style={{ display: "flex", gap: 8 }}>
              <CopyBtn text={text} />
              <Btn small variant="secondary" icon="⬇" onClick={() => {
                const docLabel = docs.find(d => d.id === active)?.label || "Document";
                const win = window.open("", "_blank", "width=860,height=700");
                const safeText = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
                win.document.write(
                  "<!DOCTYPE html><html><head><title>" + docLabel + "</title><style>" +
                  "body{font-family:Arial,sans-serif;font-size:11.5pt;line-height:1.75;color:#111;" +
                  "max-width:680px;margin:0 auto;padding:48px 40px}" +
                  "pre{white-space:pre-wrap;word-break:break-word;font-family:inherit;" +
                  "font-size:inherit;line-height:inherit;margin:0}" +
                  "h1{font-size:14pt;font-weight:bold;margin:1em 0 0.3em}" +
                  "@page{margin:2cm}" +
                  "</style></head><body>" +
                  "<h1>" + docLabel + " \u2014 " + workingRole + "</h1>" +
                  "<pre>" + safeText + "</pre>" +
                  "</body></html>"
                );
                win.document.close();
                setTimeout(() => { win.focus(); win.print(); }, 600);
              }}>PDF</Btn>
            </div>}
          </div>
          <StreamBox text={text} done={done} />
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// JOB FINDER
// ═══════════════════════════════════════════════════════════════════════════════
function JobFinder({ session, onAddToTracker, onTailor }) {
  const { brief } = session;
  const [searchState, setSearchState] = useState("idle"); // idle | searching | done
  const [jobs, setJobs] = useState([]);
  const [searchLog, setSearchLog] = useState("");
  const [customQuery, setCustomQuery] = useState("");
  const [expanded, setExpanded] = useState(null);

  const suggestedTerms = [
    ...(brief.equivalentTitles || []),
    ...(brief.searchTerms || []),
  ].slice(0, 6);

  async function search(query) {
    setSearchState("searching");
    setSearchLog("");
    setJobs([]);

    try {
      const sys = `You are a Civil Service job search assistant. Search for relevant job listings and return a JSON array of found roles. 

Return ONLY valid JSON array, no markdown:
[{
  "title": "string",
  "department": "string", 
  "grade": "string",
  "salary": "string",
  "location": "string",
  "closingDate": "string",
  "refNumber": "string or null",
  "url": "string — civilservicejobs.service.gov.uk URL if found, else null",
  "description": "string — 2-3 sentence summary of role",
  "fitScore": 0-100,
  "fitReason": "string — 2 sentences on why this matches the candidate",
  "matchedCriteria": ["string — 2-3 criteria this role shares with candidate profile"],
  "watchOuts": "string or null — any mismatch or concern"
}]

Search Civil Service Jobs (civilservicejobs.service.gov.uk) for: ${query}
Candidate profile: ${brief.grade || "Grade 7"} finance professional, ${brief.candidateName}, experience: ${(brief.keyStrengths||[]).slice(0,3).join("; ")}

Find 4-6 real, current roles. If you cannot find live listings, generate realistic representative examples that accurately reflect what is currently advertised on Civil Service Jobs for this grade and discipline.`;

      const raw = await callClaudeWithSearch(sys,
        `Find current Civil Service job listings matching: "${query}" for a ${brief.grade || "Grade 6/7"} finance professional with experience in ${(brief.equivalentTitles||["finance business partnering"]).join(", ")}. Search civilservicejobs.service.gov.uk`,
        chunk => setSearchLog(chunk)
      );

      const clean = raw.replace(/```json|```/g, "").trim();
      const start = clean.indexOf("[");
      const end = clean.lastIndexOf("]");
      if (start === -1) throw new Error("No job data found");
      const parsed = JSON.parse(clean.slice(start, end + 1));
      setJobs(parsed.sort((a, b) => b.fitScore - a.fitScore));
      setSearchState("done");
    } catch (e) {
      setSearchLog("Search error: " + e.message);
      setSearchState("done");
    }
  }

  return (
    <div className="fu">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24,
          color: C.ink, marginBottom: 6 }}>Job Finder</h2>
        <p style={{ color: C.inkMid, fontSize: 14 }}>
          Search Civil Service Jobs for equivalent roles. The platform scores each result against your profile and lets you tailor documents for any role in one click.
        </p>
      </div>

      {/* Quick search chips */}
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 11, color: C.inkDim, textTransform: "uppercase",
          letterSpacing: "0.08em", marginBottom: 10 }}>Suggested searches based on your profile</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {suggestedTerms.map((t, i) => (
            <button key={i} onClick={() => search(t)}
              disabled={searchState === "searching"}
              style={{
                padding: "6px 14px", borderRadius: 20,
                border: `1px solid ${C.violet}66`, background: `${C.violet}18`,
                color: C.violet, fontSize: 12, fontWeight: 600, cursor: "pointer",
                transition: "all 0.15s", opacity: searchState === "searching" ? 0.5 : 1,
              }}>
              🔍 {t}
            </button>
          ))}
        </div>
      </div>

      {/* Custom search */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 10 }}>
          <input value={customQuery} onChange={e => setCustomQuery(e.target.value)}
            placeholder="Search for any Civil Service role, e.g. 'Head of Finance HMRC' or 'Senior Finance Manager NHS'"
            onKeyDown={e => e.key === "Enter" && customQuery.trim() && search(customQuery)}
            style={{
              flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
              background: C.surface, color: C.ink, fontSize: 14, fontFamily: "'Outfit', sans-serif",
            }} />
          <Btn onClick={() => search(customQuery || suggestedTerms[0])}
            disabled={searchState === "searching"} variant="teal" icon="🔍">Search</Btn>
        </div>
      </Card>

      {/* Searching state */}
      {searchState === "searching" && (
        <Card className="fi" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
            <Spinner color={C.teal} />
            <span style={{ color: C.teal, fontSize: 14 }}>Searching Civil Service Jobs…</span>
          </div>
          {searchLog && (
            <div style={{ fontSize: 12, color: C.inkDim, lineHeight: 1.6,
              fontFamily: "'DM Mono', monospace", maxHeight: 120, overflowY: "auto" }}>
              {searchLog.slice(0, 400)}…
            </div>
          )}
        </Card>
      )}

      {/* Results */}
      {jobs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ fontSize: 12, color: C.inkDim, marginBottom: 4 }}>
            {jobs.length} roles found — sorted by fit score
          </p>
          {jobs.map((job, i) => (
            <Card key={i} className="fu" style={{
              border: `1px solid ${job.fitScore >= 80 ? C.green + "44" : job.fitScore >= 60 ? C.amber + "44" : C.border}`,
              transition: "border-color 0.2s",
            }}>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <FitScore score={job.fitScore} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    <div>
                      <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18,
                        color: C.ink, marginBottom: 3 }}>{job.title}</h3>
                      <p style={{ color: C.teal, fontSize: 13 }}>
                        {job.department}{job.grade ? ` · ${job.grade}` : ""}
                        {job.location ? ` · ${job.location}` : ""}
                      </p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {job.salary && <p style={{ color: C.ink, fontWeight: 600, fontSize: 14 }}>{job.salary}</p>}
                      {job.closingDate && (
                        <p style={{ color: C.red, fontSize: 12, marginTop: 2 }}>Closes: {job.closingDate}</p>
                      )}
                    </div>
                  </div>

                  <p style={{ fontSize: 13, color: C.inkMid, lineHeight: 1.6, marginBottom: 10 }}>
                    {job.description}
                  </p>

                  {/* Matched criteria */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                    {(job.matchedCriteria || []).map((c, j) => (
                      <span key={j} style={{ padding: "2px 8px", borderRadius: 4,
                        background: `${C.green}18`, border: `1px solid ${C.green}44`,
                        color: C.green, fontSize: 11 }}>✓ {c}</span>
                    ))}
                  </div>

                  {/* Expand / collapse */}
                  {expanded === i ? (
                    <div className="fi" style={{ marginBottom: 12 }}>
                      <div style={{ padding: "10px 14px", background: C.surface,
                        borderRadius: 8, marginBottom: 8, fontSize: 13, color: C.inkMid,
                        lineHeight: 1.6 }}>
                        <strong style={{ color: C.green }}>Why this fits you:</strong> {job.fitReason}
                      </div>
                      {job.watchOuts && (
                        <div style={{ padding: "10px 14px", background: `${C.amber}12`,
                          border: `1px solid ${C.amber}33`, borderRadius: 8,
                          fontSize: 13, color: C.amber }}>
                          ⚠ {job.watchOuts}
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Btn small variant="ghost" onClick={() => setExpanded(expanded === i ? null : i)}>
                      {expanded === i ? "Less ↑" : "Details ↓"}
                    </Btn>
                    <Btn small variant="teal" icon="✨"
                      onClick={() => onTailor({ advert: `${job.title} at ${job.department}\n\nGrade: ${job.grade}\nSalary: ${job.salary}\nLocation: ${job.location}\n\nDescription: ${job.description}\n\nMatched criteria: ${(job.matchedCriteria||[]).join(", ")}`, role: job.title, job })}>
                      Tailor Documents
                    </Btn>
                    <Btn small variant="secondary"
                      onClick={() => onAddToTracker({ ...job, status: "found", addedAt: new Date().toISOString() })}>
                      + Track
                    </Btn>
                    {job.url && (
                      <a href={job.url} target="_blank" rel="noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
                          color: C.inkMid, fontSize: 12, textDecoration: "none",
                          background: "transparent", transition: "all 0.15s" }}>
                        🔗 View on CS Jobs
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {searchState === "done" && jobs.length === 0 && (
        <Card>
          <p style={{ color: C.inkMid, fontSize: 14, textAlign: "center", padding: 20 }}>
            No results returned — try a different search term or check your connection.
          </p>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APPLICATION TRACKER
// ═══════════════════════════════════════════════════════════════════════════════
const STATUSES = [
  { id: "found",      label: "Found",           color: C.inkDim,  icon: "🔍" },
  { id: "tailoring",  label: "Tailoring Docs",  color: C.violet,  icon: "✏️" },
  { id: "ready",      label: "Ready to Submit", color: C.amber,   icon: "📬" },
  { id: "submitted",  label: "Submitted",       color: C.teal,    icon: "✅" },
  { id: "interview",  label: "Interview",       color: C.gold,    icon: "🎤" },
  { id: "offer",      label: "Offer / Outcome", color: C.green,   icon: "🏆" },
];

function AppTracker({ applications, onUpdate, onTailor }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newJob, setNewJob] = useState({ title: "", department: "", salary: "", closingDate: "", notes: "" });

  function addManual() {
    if (!newJob.title.trim()) return;
    onUpdate([...applications, {
      ...newJob, status: "found", addedAt: new Date().toISOString(),
      id: Date.now(),
    }]);
    setNewJob({ title: "", department: "", salary: "", closingDate: "", notes: "" });
    setShowAdd(false);
  }

  function updateStatus(id, status) {
    onUpdate(applications.map(a => a.id === id || (a.title === id) ? { ...a, status } : a));
  }

  function remove(id) {
    onUpdate(applications.filter(a => (a.id || a.title) !== id));
  }

  function updateNotes(id, notes) {
    onUpdate(applications.map(a => (a.id || a.title) === id ? { ...a, notes } : a));
  }

  const [editingNotes, setEditingNotes] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");

  return (
    <div className="fu">
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24,
            color: C.ink, marginBottom: 4 }}>Application Tracker</h2>
          <p style={{ color: C.inkMid, fontSize: 14 }}>
            {applications.length} role{applications.length !== 1 ? "s" : ""} tracked
          </p>
        </div>
        <Btn onClick={() => setShowAdd(!showAdd)} variant="secondary" icon="+">Add Manually</Btn>
      </div>

      {/* Manual add form */}
      {showAdd && (
        <Card className="sr" style={{ marginBottom: 20, border: `1px solid ${C.gold}44` }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: C.gold, marginBottom: 14 }}>Add a role manually</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {[["title","Job Title","Finance Business Partner"],["department","Department","Cabinet Office"],
              ["salary","Salary","£54,000 – £68,000"],["closingDate","Closing Date","19 March 2026"]].map(([k,l,ph]) => (
              <div key={k}>
                <label style={{ fontSize: 11, color: C.inkDim, textTransform: "uppercase",
                  letterSpacing: "0.08em", display: "block", marginBottom: 6 }}>{l}</label>
                <input value={newJob[k]} onChange={e => setNewJob(p => ({...p,[k]:e.target.value}))}
                  placeholder={ph}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8,
                    border: `1px solid ${C.border}`, background: C.surface,
                    color: C.ink, fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={addManual} variant="gold">Add Role</Btn>
            <Btn onClick={() => setShowAdd(false)} variant="secondary">Cancel</Btn>
          </div>
        </Card>
      )}

      {applications.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>📋</div>
          <p style={{ color: C.inkMid, fontSize: 15, marginBottom: 6 }}>No applications tracked yet</p>
          <p style={{ color: C.inkDim, fontSize: 13 }}>
            Use the Job Finder to discover roles and add them here, or add manually above.
          </p>
        </Card>
      ) : (
        <>
          {/* Pipeline summary */}
          <div style={{ display: "flex", gap: 0, marginBottom: 20, overflowX: "auto" }}>
            {STATUSES.map((s, i) => {
              const count = applications.filter(a => a.status === s.id).length;
              return (
                <div key={s.id} style={{ flex: 1, minWidth: 80, padding: "10px 12px",
                  background: count > 0 ? `${s.color}18` : C.surface,
                  border: `1px solid ${count > 0 ? s.color + "44" : C.border}`,
                  borderRight: i < STATUSES.length - 1 ? "none" : undefined,
                  borderRadius: i === 0 ? "8px 0 0 8px" : i === STATUSES.length - 1 ? "0 8px 8px 0" : 0,
                  textAlign: "center" }}>
                  <p style={{ fontSize: 18, marginBottom: 3 }}>{s.icon}</p>
                  <p style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, fontWeight: 700,
                    color: count > 0 ? s.color : C.inkDim }}>{count}</p>
                  <p style={{ fontSize: 10, color: C.inkDim, textTransform: "uppercase",
                    letterSpacing: "0.06em", lineHeight: 1.3 }}>{s.label}</p>
                </div>
              );
            })}
          </div>

          {/* Application cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {applications.map((app, i) => {
              const appId = app.id || app.title;
              const status = STATUSES.find(s => s.id === app.status) || STATUSES[0];
              return (
                <Card key={i} style={{ border: `1px solid ${status.color}33` }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 8,
                      background: `${status.color}22`, border: `1px solid ${status.color}44`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 18, flexShrink: 0 }}>{status.icon}</div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
                        <div>
                          <h3 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 17,
                            color: C.ink, marginBottom: 2 }}>{app.title}</h3>
                          <p style={{ color: C.teal, fontSize: 13 }}>
                            {app.department}{app.grade ? ` · ${app.grade}` : ""}
                            {app.location ? ` · ${app.location}` : ""}
                          </p>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          {app.salary && <Tag label={app.salary} color={C.inkDim} />}
                          {app.closingDate && (
                            <span style={{ fontSize: 12, color: C.red }}>⏱ {app.closingDate}</span>
                          )}
                          {app.fitScore && <FitScore score={app.fitScore} />}
                        </div>
                      </div>

                      {/* Status selector */}
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                        {STATUSES.map(s => (
                          <button key={s.id} onClick={() => updateStatus(appId, s.id)}
                            style={{
                              padding: "3px 10px", borderRadius: 12, fontSize: 11,
                              border: `1px solid ${app.status === s.id ? s.color : C.border}`,
                              background: app.status === s.id ? `${s.color}22` : "transparent",
                              color: app.status === s.id ? s.color : C.inkDim,
                              cursor: "pointer", transition: "all 0.12s", fontWeight: 600,
                            }}>{s.icon} {s.label}</button>
                        ))}
                      </div>

                      {/* Notes */}
                      {editingNotes === appId ? (
                        <div style={{ marginBottom: 10 }}>
                          <textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)}
                            rows={3} placeholder="Add notes, contacts, interview date…"
                            style={{ width: "100%", padding: "8px 12px", borderRadius: 8,
                              border: `1px solid ${C.gold}`, background: C.surface,
                              color: C.ink, fontSize: 13, fontFamily: "'Outfit',sans-serif",
                              resize: "vertical" }} />
                          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                            <Btn small variant="gold" onClick={() => {
                              updateNotes(appId, notesDraft);
                              setEditingNotes(null);
                            }}>Save</Btn>
                            <Btn small variant="secondary" onClick={() => setEditingNotes(null)}>Cancel</Btn>
                          </div>
                        </div>
                      ) : app.notes ? (
                        <div style={{ fontSize: 13, color: C.inkMid, padding: "8px 12px",
                          background: C.surface, borderRadius: 8, marginBottom: 10,
                          cursor: "pointer", lineHeight: 1.5 }}
                          onClick={() => { setEditingNotes(appId); setNotesDraft(app.notes); }}>
                          📝 {app.notes}
                        </div>
                      ) : null}

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Btn small variant="ghost" icon="✨"
                          onClick={() => onTailor({
                            advert: `${app.title} at ${app.department}\n${app.description||""}`,
                            role: app.title, job: app
                          })}>
                          Tailor Documents
                        </Btn>
                        <Btn small variant="secondary"
                          onClick={() => {
                            setEditingNotes(appId);
                            setNotesDraft(app.notes || "");
                          }}>
                          {app.notes ? "Edit Notes" : "+ Notes"}
                        </Btn>
                        <Btn small variant="danger" onClick={() => remove(appId)}>Remove</Btn>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERVIEW COACH (condensed from previous build)
// ═══════════════════════════════════════════════════════════════════════════════
function InterviewCoach({ session }) {
  const { brief, supportingDocs } = session;
  const coachDocs = (supportingDocs || []).filter(d => d.text);
  const coachContext = coachDocs.length > 0
    ? "\nADDITIONAL CANDIDATE CONTEXT:\n" + coachDocs.map(d => `[${d.label || "Document"}]:\n${d.text}`).join("\n\n")
    : "";
  const [phase, setPhase] = useState("setup");
  const [mode, setMode] = useState("panel");
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);

  const modes = [
    { id: "panel",     icon: "👥", label: "Full Panel",      desc: "Behaviours + experience + curveball" },
    { id: "technical", icon: "🔢", label: "Technical",       desc: "Challenging finance-specific questions" },
    { id: "rapid",     icon: "⚡", label: "Rapid Fire",      desc: "8 quick questions — build confidence" },
  ];

  async function startInterview() {
    setLoading(true);
    const modePrompts = {
      panel: `Generate ${(brief.behaviours||["Communicating and Influencing","Working Together","Developing Self and Others"]).length + 3} questions: one per behaviour (${(brief.behaviours||[]).join(",")}), 2 experience questions, 1 situational, 1 curveball/pressure`,
      technical: "Generate 6 challenging technical finance questions at Grade 7 level: accounting standards, Spending Reviews, risk management, business case scrutiny, financial governance",
      rapid: "Generate 8 short rapid-fire questions — mix of behavioural and situational — each answerable in 60-90 seconds",
    };
    try {
      const raw = await callClaude(
        `Return ONLY a JSON array: [{"type":"behaviour|technical|experience|situational","behaviour":"string or null","question":"string","whatWeAreLooking":"string"}]`,
        `Role: ${brief.roleTitle} | Criteria: ${(brief.essentialCriteria||[]).slice(0,4).join("; ")}\n${modePrompts[mode]}${coachContext}`,
        null
      );
      const qs = JSON.parse(raw.replace(/```json|```/g,"").trim());
      setQuestions(qs); setQIdx(0); setScores([]); setAnswer(""); setFeedback(null);
      setPhase("question");
    } catch(e) { alert("Error: " + e.message); }
    setLoading(false);
  }

  async function submitAnswer() {
    if (!answer.trim()) return;
    setPhase("feedback"); setFeedback(null);
    const q = questions[qIdx];
    try {
      const raw = await callClaude(
        `Return ONLY JSON: {"starScore":{"situation":1-5,"task":1-5,"action":1-5,"result":1-5},"overallScore":1-5,"strengths":["string","string"],"improvements":["string","string"],"missedPoints":["string"],"modelOpening":"string","panelVerdict":"string","followUpChallenge":"string"}`,
        `Role: ${brief.roleTitle}\nQuestion: ${q.question}\nBehaviour: ${q.behaviour||q.type}\nLooking for: ${q.whatWeAreLooking}\nAnswer: ${answer}${coachContext}`,
        null
      );
      const fb = JSON.parse(raw.replace(/```json|```/g,"").trim());
      setFeedback(fb);
      setScores(p => [...p, { q: q.question, s: fb.overallScore }]);
    } catch(e) { setFeedback({ overallScore:0, panelVerdict:"Error: "+e.message, starScore:{}, strengths:[], improvements:[], missedPoints:[], modelOpening:"", followUpChallenge:"" }); }
  }

  if (phase === "setup") return (
    <div className="fu">
      <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:24, color:C.ink, marginBottom:6 }}>Interview Coach</h2>
      <p style={{ color:C.inkMid, fontSize:14, marginBottom:20 }}>The AI panel asks real questions and scores your STAR answers.</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
        {modes.map(m => (
          <button key={m.id} onClick={() => setMode(m.id)} style={{
            padding:18, borderRadius:10, border:`2px solid ${mode===m.id?C.gold:C.border}`,
            background:mode===m.id?`${C.gold}12`:C.card, textAlign:"left", cursor:"pointer",
          }}>
            <div style={{fontSize:26, marginBottom:8}}>{m.icon}</div>
            <div style={{fontWeight:700, color:mode===m.id?C.gold:C.ink, marginBottom:4}}>{m.label}</div>
            <div style={{fontSize:12, color:C.inkDim}}>{m.desc}</div>
          </button>
        ))}
      </div>
      <Card style={{background:C.surface, marginBottom:20}}>
        <p style={{fontSize:12, color:C.inkDim, marginBottom:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em"}}>Behaviours being assessed</p>
        <div style={{display:"flex", flexWrap:"wrap", gap:8}}>
          {(brief.behaviours||[]).map((b,i) => <Tag key={i} label={b} color={C.teal}/>)}
        </div>
      </Card>
      {loading ? <div style={{display:"flex",gap:12,alignItems:"center"}}><Spinner/><span style={{color:C.inkMid,fontSize:14}}>Building your panel…</span></div>
        : <Btn onClick={startInterview} variant="gold" icon="▶">Begin Interview</Btn>}
    </div>
  );

  if (phase === "question") {
    const q = questions[qIdx];
    return (
      <div className="fu">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Tag label={`Q${qIdx+1}/${questions.length}`} color={C.inkDim}/>
            {q.behaviour && <Tag label={q.behaviour} color={C.teal}/>}
          </div>
          <Btn small variant="secondary" onClick={() => setPhase("setup")}>← Setup</Btn>
        </div>
        <Card style={{marginBottom:16, borderLeft:`3px solid ${C.gold}`}}>
          <p style={{fontSize:11,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,fontWeight:700}}>Panel Question</p>
          <p style={{fontFamily:"'DM Serif Display',serif", fontSize:22, color:C.ink, lineHeight:1.45}}>"{q.question}"</p>
          {q.whatWeAreLooking && <p style={{fontSize:12,color:C.inkDim,marginTop:10,fontStyle:"italic",borderTop:`1px solid ${C.border}`,paddingTop:8}}>💡 {q.whatWeAreLooking}</p>}
        </Card>
        <Card style={{background:`${C.teal}0E`,border:`1px solid ${C.teal}33`,marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
            {[["S","Situation","Set scene briefly"],["T","Task","Your responsibility"],["A","Action","What YOU did"],["R","Result","Quantified impact"]].map(([l,n,d])=>(
              <div key={l}>
                <span style={{fontWeight:700,color:C.teal,fontSize:13}}>{l} — {n}</span>
                <p style={{fontSize:11,color:C.inkMid,marginTop:2}}>{d}</p>
              </div>
            ))}
          </div>
        </Card>
        <TA label="Your Answer" placeholder="Type your answer as you would speak it… aim for 2-3 minutes of content"
          value={answer} onChange={setAnswer} rows={9}/>
        <div style={{marginTop:14,display:"flex",gap:10}}>
          <Btn onClick={submitAnswer} disabled={answer.trim().length<30} variant="gold" icon="→">Submit for Scoring</Btn>
          <Btn small variant="secondary" onClick={() => { setQIdx(i=>i+1); setAnswer(""); if(qIdx+1>=questions.length) setPhase("complete"); }}>Skip</Btn>
        </div>
      </div>
    );
  }

  if (phase === "feedback") {
    const fb = feedback;
    return (
      <div className="fu">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{fontFamily:"'DM Serif Display',serif",fontSize:22}}>Panel Feedback</h3>
          <Tag label={`Q${qIdx+1}/${questions.length}`} color={C.inkDim}/>
        </div>
        {!fb ? (
          <Card style={{display:"flex",justifyContent:"center",alignItems:"center",gap:14,padding:40}}>
            <Spinner color={C.teal}/><span style={{color:C.inkMid}}>Scoring your answer…</span>
          </Card>
        ) : (
          <>
            <Card style={{marginBottom:12, borderLeft:`3px solid ${fb.overallScore>=4?C.green:fb.overallScore>=3?C.amber:C.red}`}}>
              <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:14}}>
                <div>
                  <p style={{fontSize:11,color:C.inkDim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Overall</p>
                  <span style={{fontFamily:"'DM Serif Display',serif",fontSize:44,color:fb.overallScore>=4?C.green:fb.overallScore>=3?C.amber:C.red}}>{fb.overallScore}<span style={{fontSize:20,color:C.inkDim}}>/5</span></span>
                </div>
                <div style={{minWidth:180}}>
                  {fb.starScore && Object.entries(fb.starScore).map(([k,v])=><ScoreBar key={k} score={v} label={k}/>)}
                </div>
              </div>
              <p style={{fontSize:13,fontStyle:"italic",color:C.inkMid,lineHeight:1.65,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                <strong style={{fontStyle:"normal",color:C.inkDim}}>Panel thinking: </strong>{fb.panelVerdict}
              </p>
            </Card>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <Card>
                <p style={{fontSize:11,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>✓ Strengths</p>
                {(fb.strengths||[]).map((s,i)=><p key={i} style={{fontSize:13,color:C.inkMid,lineHeight:1.5,marginBottom:6}}>• {s}</p>)}
              </Card>
              <Card>
                <p style={{fontSize:11,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>△ Improve</p>
                {(fb.improvements||[]).map((s,i)=><p key={i} style={{fontSize:13,color:C.inkMid,lineHeight:1.5,marginBottom:6}}>• {s}</p>)}
              </Card>
            </div>
            {fb.missedPoints?.length>0 && (
              <Card style={{background:`${C.gold}0E`,border:`1px solid ${C.gold}33`,marginBottom:12}}>
                <p style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Panel wanted to hear</p>
                {fb.missedPoints.map((s,i)=><p key={i} style={{fontSize:13,color:C.inkMid,marginBottom:6}}>• {s}</p>)}
              </Card>
            )}
            {fb.modelOpening && (
              <Card style={{background:`${C.teal}0E`,border:`1px solid ${C.teal}33`,marginBottom:12}}>
                <p style={{fontSize:11,fontWeight:700,color:C.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>How a strong answer opens</p>
                <p style={{fontSize:14,fontStyle:"italic",color:C.ink}}>"{fb.modelOpening}…"</p>
              </Card>
            )}
            {fb.followUpChallenge && (
              <Card style={{borderLeft:`3px solid ${C.red}`,marginBottom:16}}>
                <p style={{fontSize:11,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>⚠ Likely follow-up</p>
                <p style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:C.ink}}>"{fb.followUpChallenge}"</p>
              </Card>
            )}
            <Btn onClick={() => {
              if(qIdx+1>=questions.length) { setPhase("complete"); }
              else { setQIdx(i=>i+1); setAnswer(""); setFeedback(null); setPhase("question"); }
            }} variant="gold" icon={qIdx+1>=questions.length?"✓":"→"}>
              {qIdx+1>=questions.length?"See Report":"Next Question"}
            </Btn>
          </>
        )}
      </div>
    );
  }

  if (phase === "complete") {
    const avg = scores.length ? (scores.reduce((a,s)=>a+s.s,0)/scores.length).toFixed(1) : "—";
    return (
      <div className="fu">
        <Card style={{background:`linear-gradient(135deg,${C.card},#1A2040)`,border:`1px solid ${C.gold}44`,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16}}>
            <div>
              <p style={{fontSize:11,color:C.gold,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>Session Score</p>
              <span style={{fontFamily:"'DM Serif Display',serif",fontSize:56,color:C.ink}}>{avg}<span style={{fontSize:24,color:C.inkDim}}>/5</span></span>
            </div>
            <div style={{maxWidth:280}}>
              <Tag label={avg>=4?"Strong — likely to progress":avg>=3?"Borderline — sharpen results":"Needs preparation"} color={avg>=4?C.green:avg>=3?C.amber:C.red}/>
              <p style={{color:C.inkMid,fontSize:13,marginTop:10,lineHeight:1.6}}>
                {avg>=4?"Good performance. Focus on quantifying results and preparing for technical follow-ups."
                :avg>=3?"Solid foundation. Priority: stronger results paragraphs and more specific evidence."
                :"Focus on STAR structure and rehearsing core examples with clear, quantified outcomes."}
              </p>
            </div>
          </div>
        </Card>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
          {scores.map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:C.card,borderRadius:8,border:`1px solid ${C.border}`}}>
              <span style={{fontSize:12,color:C.inkDim,fontFamily:"'DM Mono',monospace",width:24}}>Q{i+1}</span>
              <span style={{fontSize:13,color:C.inkMid,flex:1}}>{s.q.slice(0,80)}…</span>
              <div style={{minWidth:120}}><ScoreBar score={s.s}/></div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn onClick={()=>{setPhase("setup");setScores([]);}} variant="gold" icon="↺">Practice Again</Btn>
          <Btn onClick={()=>setPhase("setup")} variant="secondary">Change Mode</Btn>
        </div>
      </div>
    );
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CHEAT SHEET
// ═══════════════════════════════════════════════════════════════════════════════
function CheatSheet({ brief }) {
  const tips = [
    {icon:"🎯",t:"Always use 'I' not 'we'",b:"The panel scores your personal contribution. Every STAR sentence should start with 'I'.",c:C.gold},
    {icon:"📊",t:"Quantify everything",b:"£9m budget, 98% recovery rate, reduced from 18 months to 4 — always stronger than 'improved significantly'.",c:C.teal},
    {icon:"⏱",t:"Pause before you answer",b:"5 seconds to structure is impressive, not weak. Panels want organised thinking. Rushing is far worse.",c:C.violet},
    {icon:"💪",t:"Challenge is a strength",b:"Grade 7 is expected to push back on senior leaders. Frame challenge as evidence-based and collaborative.",c:C.green},
    {icon:"🔄",t:"Weak vs strong results",b:"Weak: 'Leaders accepted my recommendation.' Strong: 'Prevented an estimated in-year overspend and established the approach as the template for future planning rounds.'",c:C.amber},
    {icon:"🧠",t:"Prepare for the follow-up",b:"If you say 'I improved the process', expect: what specifically changed? How did you measure it? What resistance did you face?",c:C.red},
    {icon:"📝",t:"Close with reflection",b:"Strong STAR answers close with what the outcome enabled or what you learned. This signals maturity and continuous improvement.",c:C.teal},
    {icon:"❓",t:"Close the interview strongly",b:`Ask: "What are the biggest financial challenges facing ${brief?.department||"the team"} over the next year, and where do you see the business partnering function making the greatest difference?"`,c:C.gold},
  ];
  return (
    <div className="fu">
      <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:24,color:C.ink,marginBottom:6}}>Interview Cheat Sheet</h2>
      <p style={{color:C.inkMid,fontSize:14,marginBottom:20}}>Civil Service interview technique — the essentials</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        {tips.map((t,i)=>(
          <Card key={i} style={{borderTop:`3px solid ${t.c}`}}>
            <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
              <span style={{fontSize:22,flexShrink:0}}>{t.icon}</span>
              <div>
                <p style={{fontWeight:700,color:C.ink,fontSize:14,marginBottom:6}}>{t.t}</p>
                <p style={{fontSize:13,color:C.inkMid,lineHeight:1.6}}>{t.b}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState("brief");
  const [applications, setApplications] = useState([]);
  const [tailorTarget, setTailorTarget] = useState(null); // { advert, role, job }
  const [loaded, setLoaded] = useState(false);

  // Restore from storage on mount
  useEffect(() => {
    (async () => {
      const saved = await storeGet("session");
      const savedApps = await storeGet("applications");
      if (saved) setSession(saved);
      if (savedApps) setApplications(savedApps);
      setLoaded(true);
    })();
  }, []);

  // Persist applications
  useEffect(() => {
    if (loaded) storeSet("applications", applications);
  }, [applications, loaded]);

  function handleAnalyse(s) {
    setSession(s);
    setTab("brief");
    // Auto-add the current role to tracker
    const existing = applications.find(a => a.title === s.brief.roleTitle && a.department === s.brief.department);
    if (!existing) {
      setApplications(prev => [{
        title: s.brief.roleTitle,
        department: s.brief.department,
        grade: s.brief.grade,
        salary: s.brief.salary,
        closingDate: s.brief.closingDate,
        status: "tailoring",
        addedAt: new Date().toISOString(),
        id: Date.now(),
        notes: "Loaded from initial analysis",
      }, ...prev]);
    }
  }

  function handleAddToTracker(job) {
    const id = job.id || `${job.title}-${job.department}`;
    const exists = applications.find(a => (a.id || `${a.title}-${a.department}`) === id);
    if (!exists) {
      setApplications(prev => [{ ...job, id: job.id || Date.now() }, ...prev]);
    }
  }

  function handleTailor({ advert, role, job }) {
    setTailorTarget({ advert, role, job });
    setTab("docs");
  }

  const tabs = session ? [
    { id:"brief",     icon:"📊", label:"Brief" },
    { id:"docs",      icon:"📄", label:"Documents" },
    { id:"jobs",      icon:"🔍", label:`Job Finder` },
    { id:"tracker",   icon:"📋", label:`Tracker${applications.length>0?` (${applications.length})`:""}` },
    { id:"interview", icon:"🎤", label:"Interview" },
    { id:"tips",      icon:"💡", label:"Cheat Sheet" },
  ] : [];

  if (!loaded) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.bg}}>
      <Spinner size={28} />
    </div>
  );

  return (
    <>
      <style>{GLOBAL}</style>
      <div style={{ minHeight:"100vh", background:C.bg }}>

        {/* Nav */}
        <div style={{
          borderBottom:`1px solid ${C.border}`, background:C.surface,
          padding:"12px 28px", display:"flex", alignItems:"center",
          justifyContent:"space-between", position:"sticky", top:0, zIndex:100,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:34,height:34,background:`linear-gradient(135deg,${C.gold},${C.amber})`,
              borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>🎯</div>
            <span style={{fontFamily:"'DM Serif Display',serif",fontSize:17,color:C.ink}}>
              Career Platform
            </span>
            <span style={{fontSize:12,color:C.inkDim,fontStyle:"italic"}}>Civil Service · Grade 6/7</span>
          </div>
          {session && (
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{textAlign:"right"}}>
                <p style={{fontSize:11,color:C.inkDim}}>Loaded for</p>
                <p style={{fontSize:13,fontWeight:600,color:C.ink}}>{session.brief.candidateName}</p>
              </div>
              <Btn small variant="secondary" onClick={() => {setSession(null);setTailorTarget(null);}}>← New</Btn>
            </div>
          )}
        </div>

        <div style={{maxWidth:1000,margin:"0 auto",padding:"32px 20px"}}>
          {!session ? (
            <StepInput onAnalyse={handleAnalyse} savedSession={null}/>
          ) : (
            <>
              <TabBar tabs={tabs} active={tab} onSelect={t => { setTab(t); if(t!=="docs") setTailorTarget(null); }}/>
              {tab==="brief"     && <BriefPanel brief={session.brief}/>}
              {tab==="docs"      && (
                <DocGenerator
                  session={session}
                  targetAdvert={tailorTarget?.advert}
                  targetRole={tailorTarget?.role}
                />
              )}
              {tab==="jobs"      && (
                <JobFinder
                  session={session}
                  onAddToTracker={handleAddToTracker}
                  onTailor={handleTailor}
                />
              )}
              {tab==="tracker"   && (
                <AppTracker
                  applications={applications}
                  onUpdate={setApplications}
                  onTailor={handleTailor}
                />
              )}
              {tab==="interview" && <InterviewCoach session={session}/>}
              {tab==="tips"      && <CheatSheet brief={session.brief}/>}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{borderTop:`1px solid ${C.border}`,padding:"16px 28px",textAlign:"center"}}>
          <p style={{fontSize:12,color:C.inkDim,fontStyle:"italic"}}>
            Per Civil Service guidance on AI: all examples must be truthful and from your own experience.
            This platform prepares and organises your application — you review, own, and submit every word.
          </p>
        </div>
      </div>
    </>
  );
}
